import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/App';
import { CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchUser } = useContext(AuthContext);
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/stripe/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          setStatus('success');
          await fetchUser();
          toast.success('Payment successful!');
          return;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return;
        }
      }

      setAttempts(attempts + 1);
      setTimeout(checkPaymentStatus, 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(attempts + 1);
      setTimeout(checkPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="w-full max-w-md card-glass p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-4 border-[#FFD60A] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Processing Payment...</h2>
            <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-[#10B981]/20 border-2 border-[#10B981] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#10B981]" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Payment Successful!</h2>
            <p className="text-white/60 mb-8" style={{ fontFamily: 'Manrope' }}>Your payment has been processed successfully</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary w-full"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-[#EF4444]/20 border-2 border-[#EF4444] rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl text-[#EF4444]">×</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Payment Failed</h2>
            <p className="text-white/60 mb-8" style={{ fontFamily: 'Manrope' }}>Your payment could not be processed</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary w-full"
              data-testid="back-to-dashboard-btn"
            >
              Back to Dashboard
            </button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Check Timed Out</h2>
            <p className="text-white/60 mb-8" style={{ fontFamily: 'Manrope' }}>Please check your email for confirmation or contact support</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary w-full"
              data-testid="timeout-dashboard-btn"
            >
              Back to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;